import React, { useState, useEffect, useCallback } from 'react';
import './GuidedConversation.css';
import GuidedProcedureSelector from './GuidedProcedureSelector';
import GuidedAnimalNumbers from './GuidedAnimalNumbers';
import GuidedFoodWaterBlood from './GuidedFoodWaterBlood';
import { downloadReport, ReportFormat, ReportData } from '../../utils/protocolReport';

interface Protocol {
  id: number;
  title: string;
  entry_mode?: string;
  guided_complete?: boolean;
}

interface Phase {
  phase_number: number;
  phase_name: string;
  phase_description: string;
  is_skippable?: boolean;
}

interface PhaseContent {
  content: string;
  targetQuestions: string[];
}

interface Session {
  id: number;
  protocol_id: number;
  current_phase: number;
  phase_status: { [key: string]: { status: string; [key: string]: any } };
  is_active: boolean;
}

interface GuidedConversationProps {
  protocol: Protocol;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onComplete: () => void;
  onSwitchToProtocol: () => void;
  onTitleUpdate?: (newTitle: string) => void;
}

const GuidedConversation: React.FC<GuidedConversationProps> = ({
  protocol,
  apiBaseUrl,
  getAuthHeaders,
  onComplete,
  onSwitchToProtocol,
  onTitleUpdate
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [currentPhase, setCurrentPhase] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Content states
  const [userInput, setUserInput] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [targetQuestions, setTargetQuestions] = useState<string[]>([]);
  const [editableContent, setEditableContent] = useState('');

  // Phase 1 specific state - protocol title
  const [protocolTitle, setProtocolTitle] = useState('');

  // Phase navigation state
  const [viewingPhase, setViewingPhase] = useState<number | null>(null);
  const [savedPhaseContent, setSavedPhaseContent] = useState<{ [key: number]: PhaseContent }>({});

  // Phase 2 procedure selection state (user-driven, no AI)
  const [compiledNonsurgicalText, setCompiledNonsurgicalText] = useState('');
  const [compiledSurgicalText, setCompiledSurgicalText] = useState('');

  // Phase 3 animal numbers state
  const [animalNumbersData, setAnimalNumbersData] = useState<{
    mice: number;
    rats: number;
    otherSpecies: boolean;
    justificationText: string;
    reconciliationNote?: string;
  } | null>(null);

  // Phase 5 food/water & blood collection state
  const [phase5ShouldDisplay, setPhase5ShouldDisplay] = useState(true);

  // Initialize or resume session
  useEffect(() => {
    const initSession = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${apiBaseUrl}/api/guided-conversation/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ protocolId: protocol.id })
        });

        if (!response.ok) {
          throw new Error('Failed to start guided conversation');
        }

        const data = await response.json();
        setSession(data.session);
        setPhases(data.phases);
        setCurrentPhase(data.session.current_phase);

        // If resuming, load saved answers for completed phases
        if (data.isResumed && data.savedAnswers) {
          const phaseContent: { [key: number]: PhaseContent } = {};

          // Group answers by phase
          data.phases.forEach((phase: any) => {
            if (phase.target_questions && phase.target_questions.length > 0) {
              const phaseAnswers = phase.target_questions
                .map((q: string) => data.savedAnswers[q])
                .filter((a: string | undefined) => a);

              if (phaseAnswers.length > 0) {
                phaseContent[phase.phase_number] = {
                  content: phaseAnswers.join('\n\n'),
                  targetQuestions: phase.target_questions
                };
              }
            }
          });

          setSavedPhaseContent(phaseContent);
          console.log(`[Guided] Loaded ${Object.keys(phaseContent).length} completed phases`);
        }

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, [protocol.id, apiBaseUrl, getAuthHeaders]);

  // Get current phase info
  const currentPhaseInfo = phases.find(p => p.phase_number === currentPhase);
  const viewingPhaseInfo = viewingPhase ? phases.find(p => p.phase_number === viewingPhase) : null;

  // Check if user can switch to Protocol View (after phase 3)
  const canSwitchToProtocolView = currentPhase > 3;

  // Get phase status
  const getPhaseStatus = useCallback((phaseNumber: number): string => {
    if (!session?.phase_status) return 'pending';
    return session.phase_status[phaseNumber]?.status || 'pending';
  }, [session?.phase_status]);

  // Navigate to view a completed phase
  const handlePhaseClick = useCallback((phaseNumber: number) => {
    const status = getPhaseStatus(phaseNumber);
    if (status === 'completed' || status === 'skipped') {
      if (viewingPhase === phaseNumber) {
        // Clicking current viewing phase returns to current work
        setViewingPhase(null);
      } else {
        setViewingPhase(phaseNumber);
      }
    }
  }, [viewingPhase, getPhaseStatus]);

  // Return to current phase from viewing
  const handleReturnToCurrent = useCallback(() => {
    setViewingPhase(null);
  }, []);

  // Generate content for current phase
  const handleGenerate = useCallback(async () => {
    if (currentPhase === 1) {
      if (!protocolTitle.trim()) {
        setError('Please enter a protocol title');
        return;
      }
      if (!userInput.trim()) {
        setError('Please describe your experiment');
        return;
      }
    }

    try {
      setIsGenerating(true);
      setError(null);

      const requestBody: any = {
        phaseNumber: currentPhase,
        userInput: userInput.trim() || undefined
      };

      // Include protocol title for Phase 1
      if (currentPhase === 1) {
        requestBody.protocolTitle = protocolTitle.trim();
      }

      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.details
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || 'Failed to generate content';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Standard handling for all phases (Phase 2 no longer uses AI - user selects procedures directly)
      setGeneratedContent(data.content);
      setEditableContent(data.content);
      setTargetQuestions(data.targetQuestions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  }, [apiBaseUrl, protocol.id, currentPhase, userInput, protocolTitle, getAuthHeaders]);

  // Accept and save content
  const handleAccept = useCallback(async () => {
    if (!editableContent.trim()) {
      setError('No content to save');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // For Phase 1, include protocol title when saving
      if (currentPhase === 1) {
        const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            phaseNumber: currentPhase,
            content: editableContent,
            targetQuestions,
            protocolTitle: protocolTitle.trim()
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save content');
        }

        const data = await response.json();
        setSession(data.session);

        // Show success message
        console.log(`[Guided] Saved Phase 1: Title to Q1.1, Experimental Design to Q4.2`);
        setSuccessMessage(`Saved: ${targetQuestions.join(', ')}`);
        setTimeout(() => setSuccessMessage(null), 3000);

        // Update protocol title in parent component for immediate UI refresh
        if (onTitleUpdate && protocolTitle.trim()) {
          onTitleUpdate(protocolTitle.trim());
        }
      } else if (currentPhase === 2 && (compiledNonsurgicalText || compiledSurgicalText)) {
        // For Phase 2 with compiled procedures, save separately to Q4.4 and Q4.5
        // Parse the editable content to split back into Q4.4 and Q4.5
        // The user may have edited the combined text, so we need to preserve their changes
        // IMPORTANT: Split on the section header, not '---', because '---' is also used to separate individual procedures
        const surgicalHeaderMatch = editableContent.match(/## Surgical Procedures \(Q4\.5\)/);
        let nonsurgicalContent = compiledNonsurgicalText;
        let surgicalContent = compiledSurgicalText;

        if (surgicalHeaderMatch) {
          const surgicalIndex = surgicalHeaderMatch.index!;
          nonsurgicalContent = editableContent.substring(0, surgicalIndex)
            .replace(/^## Non-Surgical Procedures \(Q4\.4\)\n\n/, '')
            .replace(/\n\n---\n\n$/, '') // Remove trailing separator if present
            .trim();
          surgicalContent = editableContent.substring(surgicalIndex)
            .replace(/^## Surgical Procedures \(Q4\.5\)\n\n/, '')
            .trim();
        } else {
          // Fallback: if no surgical header found, assume entire content is nonsurgical
          nonsurgicalContent = editableContent.replace(/^## Non-Surgical Procedures \(Q4\.4\)\n\n/, '').trim();
        }

        // Save Q4.4 (non-surgical) - include followupData to set Yes/No radio
        // followupData[1] = 'Yes' tells Protocol View to show the procedures section
        const hasNonsurgical = nonsurgicalContent && nonsurgicalContent !== 'None selected';
        await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            phaseNumber: currentPhase,
            content: nonsurgicalContent,
            targetQuestions: ['4.4'],
            followupData: ['', hasNonsurgical ? 'Yes' : 'No']
          })
        });

        // Save Q4.5 (surgical) - include followupData to set Yes/No radio
        // Note: Q4.5 uses followupData[0] for Yes/No (unlike Q4.4 which uses [1])
        const hasSurgical = surgicalContent && surgicalContent !== 'None selected';
        const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            phaseNumber: currentPhase,
            content: surgicalContent,
            targetQuestions: ['4.5'],
            followupData: [hasSurgical ? 'Yes' : 'No']
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save content');
        }

        const data = await response.json();
        setSession(data.session);

        // Show success message
        console.log(`[Guided] Saved Phase 2: Q4.4 (Nonsurgical), Q4.5 (Surgical)`);
        setSuccessMessage('Saved: Q4.4, Q4.5');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (currentPhase === 3 && animalNumbersData) {
        // Phase 3: Save justification to Q4.3 and optionally update Q4.2 with reconciliation note

        // Prepare followupData for Q4.3: [mice, rats, otherSpecies]
        const q43FollowupData = [
          String(animalNumbersData.mice),
          String(animalNumbersData.rats),
          animalNumbersData.otherSpecies ? 'Yes' : 'No'
        ];

        // First, save the justification to Q4.3 with followupData
        const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            phaseNumber: currentPhase,
            content: animalNumbersData.justificationText,
            targetQuestions: ['4.3'],
            followupData: q43FollowupData
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save content');
        }

        const data = await response.json();
        setSession(data.session);

        // If there's a reconciliation note, append it to Q4.2
        if (animalNumbersData.reconciliationNote) {
          try {
            await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/append-note`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
              },
              body: JSON.stringify({
                questionNumber: '4.2',
                note: animalNumbersData.reconciliationNote
              })
            });
            console.log(`[Guided] Appended reconciliation note to Q4.2`);
          } catch (appendError) {
            console.error('[Guided] Failed to append reconciliation note:', appendError);
            // Don't fail the whole save if just the note append fails
          }
        }

        // Show success message
        const savedQuestions = animalNumbersData.reconciliationNote ? 'Q4.3, Q4.2 (note)' : 'Q4.3';
        console.log(`[Guided] Saved Phase 3: ${savedQuestions}`);
        setSuccessMessage(`Saved: ${savedQuestions}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // Standard handling for other phases
        const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            phaseNumber: currentPhase,
            content: editableContent,
            targetQuestions
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save content');
        }

        const data = await response.json();
        setSession(data.session);

        // Show success message
        console.log(`[Guided] Saved Phase ${currentPhase}: ${targetQuestions.join(', ')}`);
        setSuccessMessage(`Saved: ${targetQuestions.join(', ')}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }

      // Move to next phase
      const nextPhase = currentPhase + 1;
      if (nextPhase <= 8) {
        setCurrentPhase(nextPhase);
        setUserInput('');
        setGeneratedContent('');
        setEditableContent('');
        setTargetQuestions([]);
        // Reset Phase 1 specific state
        setProtocolTitle('');
        // Reset Phase 2 specific state
        setCompiledNonsurgicalText('');
        setCompiledSurgicalText('');
        // Reset Phase 3 specific state
        setAnimalNumbersData(null);
      } else {
        // Conversation complete
        onComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setIsSaving(false);
    }
  }, [apiBaseUrl, protocol.id, currentPhase, editableContent, targetQuestions, getAuthHeaders, onComplete, compiledNonsurgicalText, compiledSurgicalText]);

  // Skip current phase
  const handleSkip = useCallback(async () => {
    try {
      setIsSaving(true);
      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ phaseNumber: currentPhase })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Cannot skip this phase');
      }

      const data = await response.json();
      setSession(data.session);
      setCurrentPhase(currentPhase + 1);
      setUserInput('');
      setGeneratedContent('');
      setEditableContent('');
      // Reset Phase 1 specific state
      setProtocolTitle('');
      // Reset Phase 2 specific state
      setCompiledNonsurgicalText('');
      setCompiledSurgicalText('');
      // Reset Phase 3 specific state
      setAnimalNumbersData(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip phase');
    } finally {
      setIsSaving(false);
    }
  }, [apiBaseUrl, protocol.id, currentPhase, getAuthHeaders]);

  // Complete the conversation
  const handleComplete = useCallback(async () => {
    try {
      setIsSaving(true);
      await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete');
    } finally {
      setIsSaving(false);
    }
  }, [apiBaseUrl, protocol.id, getAuthHeaders, onComplete]);

  // Download a report (Word/Markdown) of the completed sections to copy into InfoEd
  const [downloadingReport, setDownloadingReport] = useState<ReportFormat | null>(null);
  const handleDownloadReport = useCallback(async (format: ReportFormat) => {
    try {
      setDownloadingReport(format);
      const response = await fetch(`${apiBaseUrl}/api/protocols/${protocol.id}/summary`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to load protocol summary');
      const data: ReportData = await response.json();
      downloadReport(data, protocol.title, format);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setDownloadingReport(null);
    }
  }, [apiBaseUrl, protocol.id, protocol.title, getAuthHeaders]);

  // Regenerate content
  const handleRegenerate = () => {
    setGeneratedContent('');
    setEditableContent('');
    // Reset Phase 2 state if applicable
    setCompiledNonsurgicalText('');
    setCompiledSurgicalText('');
    // Reset Phase 3 state if applicable
    setAnimalNumbersData(null);
    handleGenerate();
  };

  // Handle compiled procedures from the selector
  const handleProceduresCompiled = useCallback((nonsurgicalText: string, surgicalText: string) => {
    setCompiledNonsurgicalText(nonsurgicalText);
    setCompiledSurgicalText(surgicalText);
    // Combine the text for display/editing
    const combinedText = `## Non-Surgical Procedures (Q4.4)\n\n${nonsurgicalText}\n\n---\n\n## Surgical Procedures (Q4.5)\n\n${surgicalText}`;
    setGeneratedContent(combinedText);
    setEditableContent(combinedText);
  }, []);

  // Handle procedure selector errors
  const handleProcedureSelectorError = useCallback((errorMsg: string) => {
    setError(errorMsg);
  }, []);

  // Handle animal numbers accepted from Phase 3
  const handleAnimalNumbersAccepted = useCallback((data: {
    mice: number;
    rats: number;
    otherSpecies: boolean;
    justificationText: string;
    reconciliationNote?: string;
  }) => {
    setAnimalNumbersData(data);
    // Set up the content for saving
    setGeneratedContent(data.justificationText);
    setEditableContent(data.justificationText);
    setTargetQuestions(['4.3']);
  }, []);

  // Handle Phase 5 food/water & blood collection accepted
  const handleFoodWaterBloodAccepted = useCallback(async (data: any) => {
    try {
      setIsSaving(true);
      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          phaseNumber: 5,
          content: '',
          targetQuestions: ['5.3', '5.4'],
          followupData: data
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      const result = await response.json();
      setSession(result.session);
      setCurrentPhase(result.session.current_phase);
      setSuccessMessage('Food/water restriction and blood collection saved!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [apiBaseUrl, protocol.id, getAuthHeaders]);

  // Handle Phase 5 skip
  const handlePhase5Skip = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocol.id}/skip-phase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ phaseNumber: 5 })
      });

      if (!response.ok) throw new Error('Failed to skip phase');

      const result = await response.json();
      setSession(result.session);
      setCurrentPhase(result.session.current_phase);
      setSuccessMessage('Phase 5 skipped');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to skip phase');
    }
  }, [apiBaseUrl, protocol.id, getAuthHeaders]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter or Cmd+Enter to generate/accept
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (generatedContent && editableContent.trim()) {
          handleAccept();
        } else if (!generatedContent && (currentPhase !== 1 || userInput.trim())) {
          handleGenerate();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [generatedContent, editableContent, currentPhase, userInput, handleAccept, handleGenerate]);

  if (isLoading) {
    return (
      <div className="guided-conversation">
        <div className="guided-container">
          <div className="guided-loading">
            <div className="loading-spinner"></div>
            <p>Loading guided conversation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guided-conversation">
      <div className="guided-container">
      {/* Header */}
      <div className="guided-header">
        <div className="guided-header-left">
          <h1>Guided Protocol Creation</h1>
          <p className="protocol-title">{protocol.title}</p>
        </div>
        <div className="guided-header-right">
          {canSwitchToProtocolView && (
            <button
              className="switch-view-button"
              onClick={onSwitchToProtocol}
            >
              Switch to Protocol View
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer banner */}
      <div className="disclaimer-banner">
        <strong>Note:</strong> AI-generated content is provided as a starting point.
        Recommendations may go beyond strict requirements.
        Feel free to edit, simplify, or delete any content before accepting.
      </div>

      {/* Progress indicator */}
      <div className="phase-progress">
        {phases.map((phase) => {
          const status = getPhaseStatus(phase.phase_number);
          const isCurrent = phase.phase_number === currentPhase && !viewingPhase;
          const isViewing = phase.phase_number === viewingPhase;
          const isCompleted = status === 'completed';
          const isSkipped = status === 'skipped';
          const isClickable = isCompleted || isSkipped;

          return (
            <div
              key={phase.phase_number}
              className={`phase-indicator ${isCurrent ? 'current' : ''} ${isViewing ? 'viewing' : ''} ${isCompleted ? 'completed' : ''} ${isSkipped ? 'skipped' : ''} ${isClickable ? 'clickable' : ''}`}
              title={isClickable ? `Click to review ${phase.phase_name}` : phase.phase_name}
              onClick={() => isClickable && handlePhaseClick(phase.phase_number)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              <div className="phase-number">
                {isCompleted ? '✓' : isSkipped ? '–' : phase.phase_number}
              </div>
              <div className="phase-label">{phase.phase_name}</div>
            </div>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <div className="guided-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Success message display */}
      {successMessage && (
        <div className="guided-success">
          <span>✓ {successMessage}</span>
        </div>
      )}

      {/* Viewing banner when reviewing a completed phase */}
      {viewingPhase && (
        <div className="viewing-banner">
          <span>Viewing completed Phase {viewingPhase}: {viewingPhaseInfo?.phase_name}</span>
          <button className="return-button" onClick={handleReturnToCurrent}>
            Return to Phase {currentPhase}
          </button>
        </div>
      )}

      {/* Main content area */}
      <div className="guided-main">
        {currentPhase <= 8 ? (
          <>
            {/* Phase header */}
            <div className="gc-phase-header">
              {viewingPhase ? (
                <>
                  <h2>Phase {viewingPhase}: {viewingPhaseInfo?.phase_name} (Completed)</h2>
                  <p className="phase-description">
                    This phase has been completed. The content is saved to your protocol.
                  </p>
                </>
              ) : (
                <>
                  <h2>Phase {currentPhase}: {currentPhaseInfo?.phase_name}</h2>
                  <p className="phase-description">{currentPhaseInfo?.phase_description}</p>
                </>
              )}
            </div>

            {/* Viewing a completed phase - show read-only content */}
            {viewingPhase ? (
              <div className="viewing-content">
                <p className="viewing-note">
                  Content from this phase has been saved to your protocol.
                  Switch to Protocol View to edit individual questions.
                </p>
                {savedPhaseContent[viewingPhase] && (
                  <div className="saved-content-display">
                    <h4>Saved Content ({savedPhaseContent[viewingPhase].targetQuestions.join(', ')})</h4>
                    <div className="saved-content-text">
                      {savedPhaseContent[viewingPhase].content}
                    </div>
                  </div>
                )}
              </div>
            ) : currentPhase === 8 ? (
              <div className="review-phase">
                <div className="review-summary">
                  <h3>Protocol sections completed:</h3>
                  <ul className="completed-sections">
                    {phases.slice(0, 7).map(phase => {
                      const status = getPhaseStatus(phase.phase_number);
                      return (
                        <li key={phase.phase_number} className={status}>
                          <span className="status-icon">
                            {status === 'completed' ? '✓' : status === 'skipped' ? '–' : '○'}
                          </span>
                          {phase.phase_name}
                          {status === 'skipped' && <span className="skipped-note">(skipped)</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="review-actions">
                  <p>You've completed the guided conversation! Your protocol now has content for the main writing sections.</p>
                  <p><strong>Download your draft</strong> to edit and copy into InfoEd — no need to keep this form open:</p>
                  <div className="review-buttons">
                    <button
                      className="download-report-button"
                      onClick={() => handleDownloadReport('word')}
                      disabled={downloadingReport !== null}
                      title="Download a Word document of your completed sections"
                    >
                      {downloadingReport === 'word' ? 'Generating…' : '⬇ Download Word'}
                    </button>
                    <button
                      className="download-report-button"
                      onClick={() => handleDownloadReport('markdown')}
                      disabled={downloadingReport !== null}
                      title="Download a Markdown file of your completed sections"
                    >
                      {downloadingReport === 'markdown' ? 'Generating…' : '⬇ Download Markdown'}
                    </button>
                  </div>
                  <p><strong>Next steps:</strong></p>
                  <ul>
                    <li>Switch to Protocol View to review and edit any section</li>
                    <li>Complete administrative sections (Personnel, Locations, Approvals)</li>
                    <li>Use the Summary view to export your protocol</li>
                  </ul>
                  <div className="review-buttons">
                    <button
                      className="complete-button"
                      onClick={handleComplete}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Completing...' : 'Complete & Go to Protocol View'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Phase 2: User-driven procedure selection (no AI) */}
                {currentPhase === 2 && !generatedContent && (
                  <GuidedProcedureSelector
                    protocolId={protocol.id}
                    apiBaseUrl={apiBaseUrl}
                    getAuthHeaders={getAuthHeaders}
                    onCompiled={handleProceduresCompiled}
                    onError={handleProcedureSelectorError}
                  />
                )}

                {/* Phase 3: Animal Numbers with power analysis */}
                {currentPhase === 3 && !generatedContent && (
                  <GuidedAnimalNumbers
                    protocolId={protocol.id}
                    apiBaseUrl={apiBaseUrl}
                    getAuthHeaders={getAuthHeaders}
                    onAccepted={handleAnimalNumbersAccepted}
                    onError={(errorMsg) => setError(errorMsg)}
                  />
                )}

                {/* Phase 5: Food/Water & Blood Collection */}
                {currentPhase === 5 && !viewingPhase && (
                  <div className="phase-content">
                    {phase5ShouldDisplay ? (
                      <GuidedFoodWaterBlood
                        protocolId={protocol.id}
                        apiBaseUrl={apiBaseUrl}
                        getAuthHeaders={getAuthHeaders}
                        onAccepted={handleFoodWaterBloodAccepted}
                        onError={setError}
                        onSkip={handlePhase5Skip}
                      />
                    ) : (
                      <div className="phase-not-applicable">
                        <p>No food/water restriction or blood collection detected in your experimental design.</p>
                        <button onClick={handlePhase5Skip} className="skip-button">
                          Skip to Next Phase
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Input area - shown when no generated content (except Phase 2/3/5 which have custom UIs) */}
                {!generatedContent && currentPhase !== 2 && currentPhase !== 3 && currentPhase !== 5 && (
                  <div className="input-section">
                    {currentPhase === 1 ? (
                      <>
                        <label htmlFor="protocol-title-input">
                          What would you like to name this protocol?
                        </label>
                        <input
                          id="protocol-title-input"
                          type="text"
                          value={protocolTitle}
                          onChange={(e) => setProtocolTitle(e.target.value)}
                          placeholder="Enter a descriptive title for your protocol (5-15 words)"
                          disabled={isGenerating}
                          className="protocol-title-input"
                        />
                        <label htmlFor="experiment-input" style={{ marginTop: '1.5rem', display: 'block' }}>
                          Tell me about your experiment. What are you trying to accomplish, and how will you use animals?
                        </label>
                        <textarea
                          id="experiment-input"
                          value={userInput}
                          onChange={(e) => setUserInput(e.target.value)}
                          placeholder="Describe your experiment here. Include:
• What you're trying to learn or prove (hypothesis)
• What animals you'll use and how many
• What you'll do to the animals (procedures)
• How long the experiment will last

Don't worry about perfect language - I'll help you format it for IACUC."
                          rows={12}
                          disabled={isGenerating}
                        />
                      </>
                    ) : (
                      <div className="auto-generate-prompt">
                        <p>I'll generate content for this phase based on what you've already told me.</p>
                        {currentPhase > 1 && (
                          <p className="context-note">
                            Using context from: {phases.slice(0, currentPhase - 1).map(p => p.phase_name).join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="input-actions">
                      <button
                        className="generate-button"
                        onClick={handleGenerate}
                        disabled={isGenerating || (currentPhase === 1 && (!userInput.trim() || !protocolTitle.trim()))}
                      >
                        {isGenerating ? (
                          <>
                            <span className="spinner"></span>
                            Generating...
                          </>
                        ) : (
                          currentPhase === 1 ? 'Generate Experimental Design' : 'Generate Content'
                        )}
                      </button>
                      {currentPhaseInfo?.is_skippable && (
                        <button
                          className="skip-button"
                          onClick={handleSkip}
                          disabled={isSaving}
                        >
                          Skip this phase
                        </button>
                      )}
                    </div>
                    <p className="keyboard-hint">
                      Tip: Press Ctrl+Enter to {generatedContent ? 'accept' : 'generate'}
                    </p>
                  </div>
                )}

                {/* Generated content area */}
                {generatedContent && (
                  <div className="generated-section">
                    <div className="generated-header">
                      <h3>Generated Content</h3>
                      <p className="target-questions">
                        Will be saved to: {targetQuestions.join(', ') || 'protocol'}
                      </p>
                    </div>
                    <div className="generated-content">
                      <textarea
                        value={editableContent}
                        onChange={(e) => setEditableContent(e.target.value)}
                        rows={15}
                        className="editable-content"
                      />
                    </div>
                    <div className="generated-actions">
                      <button
                        className="accept-button"
                        onClick={handleAccept}
                        disabled={isSaving || !editableContent.trim()}
                      >
                        {isSaving ? 'Saving...' : 'Accept & Continue'}
                      </button>
                      <button
                        className="regenerate-button"
                        onClick={handleRegenerate}
                        disabled={isGenerating}
                      >
                        Regenerate
                      </button>
                      <button
                        className="dismiss-button"
                        onClick={() => {
                          setGeneratedContent('');
                          setEditableContent('');
                        }}
                      >
                        Start Over
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="conversation-complete">
            <h2>Guided Mode Complete!</h2>
            <p>Your protocol is ready for final review.</p>
            <button onClick={onComplete}>Go to Protocol View</button>
          </div>
        )}
      </div>

      {/* Mode lock notice */}
      {!canSwitchToProtocolView && currentPhase <= 3 && (
        <div className="mode-lock-notice">
          Complete phases 1-3 to unlock Protocol View switching
        </div>
      )}
      </div>
    </div>
  );
};

export default GuidedConversation;
